import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Block } from '@/components/Block'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import {
  useDistributionBannerMixGrid,
  useUpdateDistributionBannerMix,
} from '@/hooks/queries/crt/useDistributionBannerMix'
import { useCurrentDistributionMarginMix } from '@/hooks/queries/crt/useDistributionMarginMix'
import { crtPeriodToParams } from '@/lib/utils/crtPeriodParams'
import { UpdateDistributionBannerMix } from '@repo/backend/crt'
import { Button } from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BannerMixCellEdits,
  DistributionBannerMixDataTable,
} from './-components/DistributionBannerMixDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/insights/distribution-margin-mix/banner-mix',
)({
  component: RouteComponent,
})

const cellKey = (accountTypeId: string, productHierarchyCode: number) =>
  `${accountTypeId}|${productHierarchyCode}`

function RouteComponent() {
  const { t } = useTranslation('crt')

  const { data, isPending, error } = useDistributionBannerMixGrid()
  const { mutate: updateBannerMix, isPending: isSaving } = useUpdateDistributionBannerMix()
  const { data: currentDmm } = useCurrentDistributionMarginMix()

  const [edited, setEdited] = useState<BannerMixCellEdits>(new Map())

  const distributors = data?.distributors ?? []
  const rows = data?.rows ?? []

  const getOriginalValue = (accountTypeId: string, phCode: number): string | null => {
    const row = rows.find((r) => r.productHierarchyCode === phCode)
    return row?.values[accountTypeId] ?? null
  }

  const handleCellChange = (
    accountTypeId: string,
    productHierarchyCode: number,
    decimalValue: string,
  ) => {
    setEdited((prev) => {
      const next = new Map(prev)
      const key = cellKey(accountTypeId, productHierarchyCode)
      const original = getOriginalValue(accountTypeId, productHierarchyCode)
      const normalized = decimalValue === '' ? null : decimalValue

      const originalAsString = original ?? null
      if (originalAsString === normalized) {
        next.delete(key)
      } else {
        next.set(key, normalized)
      }
      return next
    })
  }

  const handleBulkEdit = (productHierarchyCodes: number[], values: Record<string, string>) => {
    const updates: UpdateDistributionBannerMix['updates'] = []
    const touchedKeys: string[] = []

    for (const phCode of productHierarchyCodes) {
      for (const [accountTypeId, decimalValue] of Object.entries(values)) {
        const original = getOriginalValue(accountTypeId, phCode)
        const normalized = decimalValue === '' ? null : decimalValue
        if ((original ?? null) === normalized) continue
        updates.push({
          accountTypeId,
          productHierarchyCode: phCode,
          bannerMix: normalized === null ? null : Number(normalized),
        })
        touchedKeys.push(cellKey(accountTypeId, phCode))
      }
    }

    if (updates.length === 0) return

    updateBannerMix(
      { updates },
      {
        onSuccess: () => {
          setEdited((prev) => {
            if (prev.size === 0) return prev
            const next = new Map(prev)
            for (const key of touchedKeys) {
              next.delete(key)
            }
            return next.size === prev.size ? prev : next
          })
        },
      },
    )
  }

  const handleSaveChanges = () => {
    if (edited.size === 0) return

    const updates: UpdateDistributionBannerMix['updates'] = []
    edited.forEach((decimalValue, key) => {
      const [accountTypeId, phCodeStr] = key.split('|')
      if (!accountTypeId || !phCodeStr) return
      updates.push({
        accountTypeId,
        productHierarchyCode: Number(phCodeStr),
        bannerMix: decimalValue === null || decimalValue === '' ? null : Number(decimalValue),
      })
    })

    updateBannerMix(
      { updates },
      {
        onSuccess: () => {
          setEdited(new Map())
        },
      },
    )
  }

  const areChangesPending = edited.size > 0

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <Spread>
        <PageHeader>{t('distributionBannerMix.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <Button type="button" variant="secondary" asChild>
            {currentDmm?.id ? (
              <Link
                to="/crt/customer-investments/insights/distribution-margin-mix/$year/$month"
                params={crtPeriodToParams(currentDmm.crtPeriod)}
              >
                <ArrowLeftIcon className="size-4" />
                {t('distributionBannerMix.backToMix')}
              </Link>
            ) : (
              <Link to="/crt/customer-investments/insights/distribution-margin-mix">
                <ArrowLeftIcon className="size-4" />
                {t('distributionBannerMix.backToMix')}
              </Link>
            )}
          </Button>
        </HStack>
      </Spread>

      <DistributionBannerMixDataTable
        distributors={distributors}
        rows={rows}
        isPending={isPending}
        error={error}
        edited={edited}
        onCellChange={handleCellChange}
        onBulkEdit={handleBulkEdit}
      />

      <PermissionsGuard required={['crt_distribution_banner_mix_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
